/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import { EnhancedDialog } from 'components/enhanced/dialog/EnhancedDialog';
import { formatDate } from 'components/helpers/formatters';
import { IChecklist, IChecklistSection, IChecklistTemplate } from 'features/inventory/checklist';
import { equipmentCheckListService } from 'features/inventory/checklist/api/checklist.service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompileSections } from '../CompileSections';
/**
 * Component configurations tracking operational dialog property mutations.
 */
export interface ChecklistEditDialogProps {
  /** Controls the modal open visibility state. Inherited from MUI Dialog wrapper. */
  open: boolean;
  /** Triggered upon pressing escape nodes, cancel buttons, or overlay backdrop nodes. */
  onClose: () => void;
  /** Dynamic polymorphic row data mapping containing real instance metrics or phantom placeholders. */
  rowData:
    | (IChecklist & { is_phantom?: boolean; templateId?: number })
    | (IChecklistTemplate & { is_phantom?: boolean; templateId?: number })
    | null;
  /** Primary equipment machine identifier scoped to the active parent dashboard engine layout. */
  equipmentId: number | any;
  /** Callback fired upon completion of backend transactions to sync parent data layouts. */
  onSaveSuccess: (data: any) => void;
}

/**
 * An enterprise-grade modal dialogue component built for MUI v6 that manages checklist task execution.
 * Handles dual execution pathways: provisioning phantom model templates or updates to existing instance rows.
 *
 * @example
 * ```tsx
 * <ChecklistCompileDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   rowData={selectedGridRow}
 *   equipmentId={103}
 *   onSaveSuccess={(data) => refreshLayout(data)}
 * />
 * ```
 */
export const ChecklistCompileDialog = ({
  open,
  onClose,
  rowData,
  equipmentId,
  onSaveSuccess
}: ChecklistEditDialogProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [frequency, setFrequency] = useState<string>('daily');
  const [description, setDescription] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [sections, setSections] = useState<IChecklistSection[]>([]);

  const [auditTrailInfo, setAuditTrailInfo] = useState<string>('—');

  // Rileviamo se l'elemento passato è un phantom (template) o un'istanza reale
  const isPhantom = !!(rowData?.is_phantom || (rowData && !('date' in rowData)));

  console.log('EQUIPMENT ID %s', { equipmentId });
  useEffect(() => {
    if (open && rowData) {
      setName(rowData.name || '');
      setFrequency(rowData.frequency || 'daily');
      setDescription(rowData.description || '');
      setNotes(rowData.notes || '');

      if (isPhantom) {
        const templateData = rowData as unknown as IChecklistTemplate;

        // Mappiamo i dati dal template per la visualizzazione iniziale
        const mappedSections: IChecklistSection[] = (templateData.sections || []).map(
          (tSection) => ({
            id: tSection.id, // Manteniamo temporaneamente l'ID del template per la gestione dello stato locale
            checklist: 0,
            template_section: tSection.id,
            title: tSection.title,
            sequence_number: tSection.sequence_number,
            period: tSection.period,
            items: (tSection.items || []).map((tItem) => ({
              id: tItem.id, // Manteniamo temporaneamente l'ID del template per lo stato locale
              section: 0,
              template_item: tItem.id,
              sequence_number: tItem.sequence_number,
              description: tItem.description,
              is_completed: false,
              notes: ''
            }))
          })
        );

        setSections(mappedSections);
      } else {
        setAuditTrailInfo(`- ${formatDate(rowData.updated_at)}`);

        const checklistData = rowData as IChecklist;
        setSections(checklistData.sections || []);
      }
    }
  }, [open, rowData, isPhantom]);

  // Gestione Checkbox
  const handleItemCheckChange = (sectionIndex: number, itemIndex: number, checked: boolean) => {
    // setSections((prev) => {
    //   const updated = [...prev];
    //   const updatedItems = [...updated[sectionIndex].items];
    //   updatedItems[itemIndex] = { ...updatedItems[itemIndex], is_completed: checked };
    //   updated[sectionIndex] = { ...updated[sectionIndex], items: updatedItems };
    //   return updated;
    // });
    setSections((prevSections) =>
      prevSections.map((section, sIdx) => {
        // Se non è la sezione cliccata, la restituisce inalterata
        if (sIdx !== sectionIndex) return section;

        // Se è la sezione corretta, mappa e aggiorna solo l'item selezionato
        return {
          ...section,
          items: (section.items || []).map((item, iIdx) => {
            if (iIdx !== itemIndex) return item;

            return {
              ...item,
              is_completed: checked,
              // Opzionale: aggiorna il timestamp locale se non è un template phantom
              updated_at: !isPhantom ? new Date().toISOString() : item.updated_at
            };
          })
        };
      })
    );
  };

  const handleSave = async () => {
    if (!rowData) return;
    setLoading(true);

    try {
      const allItems = sections.flatMap((s) => s.items);
      const isAllCompleted = allItems.length > 0 && allItems.every((i) => i.is_completed);

      if (isPhantom) {
        //  SCENARIO: CREAZIONE DA TEMPLATE (PULIZIA ID PHANTOM)

        const cleanedSections = sections.map((s) => ({
          // Se s.template_section non è definito, usiamo s.id SOLO se è un numero valido
          template_section: s.template_section || (typeof s.id === 'number' ? s.id : 0),
          title: s.title,
          sequence_number: s.sequence_number || 0,
          items: (s.items || []).map((i) => ({
            template_item: i.template_item || (typeof i.id === 'number' ? i.id : 0),
            description: i.description,
            sequence_number: i.sequence_number || 0,
            is_completed: !!i.is_completed,
            notes: i.notes || ''
          }))
        }));

        const responseData = await equipmentCheckListService.createFromTemplate({
          template: rowData.templateId || rowData.id,
          equipment: Number(equipmentId),
          date: new Date().toISOString().split('T')[0],
          name: name,
          notes: notes,
          description: description,
          frequency: frequency,
          sections: cleanedSections
        });

        onSaveSuccess(responseData);
      } else {
        //  SCENARIO: MODIFICA ISTANZA ESISTENTE (ID REALI NUMERICI)
        const response = await equipmentCheckListService.update(rowData.id, {
          name,
          frequency,
          description,
          notes,
          equipment: equipmentId,
          template: (rowData as IChecklist).template,
          date: (rowData as IChecklist).date,
          sections: sections
        } as any);
        onSaveSuccess(response.data);
      }

      onClose();
    } catch (error) {
      console.error('Errore riscontrato durante il salvataggio:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalItemsCount = sections.reduce((acc, sec) => acc + (sec.items?.length || 0), 0);
  const completedItemsCount = sections.reduce(
    (acc, sec) => acc + (sec.items?.filter((i) => i.is_completed).length || 0),
    0
  );

  return (
    <EnhancedDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title={`Modifica ${name}`}
      subTitle={
        !isPhantom
          ? `Ultima modifica: ${rowData?.last_modified_by_detail?.full_name} ${auditTrailInfo}`
          : ''
      }
      isSubmitting={loading}
      submitLabel={t('actions.save', 'Salva')}
      cancelLabel={t('actions.reset', 'Annulla')}
      maxWidth="md">
      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome Checklist"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Frequenza"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              fullWidth
              disabled={loading}
              sx={{
                '& .MuiInputBase-root': { borderRadius: 1.5 }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descrizione / Istruzioni"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0F334A' }}>
            Checklist da compilare
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {completedItemsCount} voci di {totalItemsCount} compilate
          </Typography>
        </Box>

        <CompileSections
          handleItemCheckChange={handleItemCheckChange}
          loading={loading}
          isTemplate={isPhantom}
          sections={sections}
        />

        <Divider />

        <TextField
          label="Note generali dell'operatore"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder="Aggiungi note per questa esecuzione..."
          disabled={loading}
        />
      </Stack>
    </EnhancedDialog>
  );
};
